import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Squares2X2Icon,
  ShoppingBagIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  PhotoIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  /** Route prefixes that should highlight this nav item */
  matchPrefixes?: string[];
}

const NAV_ITEMS: NavItem[] = [
  {
    path: '/provider/catalog',
    label: 'CATALOG',
    icon: <Squares2X2Icon className="w-6 h-6" />,
    matchPrefixes: ['/provider/catalog'],
  },
  {
    path: '/provider/orders',
    label: 'ORDERS',
    icon: <ShoppingBagIcon className="w-6 h-6" />,
    matchPrefixes: ['/provider/orders'],
  },
  {
    path: '/provider/merchants',
    label: 'MERCHANTS',
    icon: <UserGroupIcon className="w-6 h-6" />,
  },
  {
    path: '/provider/photos',
    label: 'PHOTOS',
    icon: <PhotoIcon className="w-6 h-6" />,
  },
  {
    path: '/provider/analytics',
    label: 'ANALYTICS',
    icon: <ChartBarIcon className="w-6 h-6" />,
  },
  {
    path: '/provider/settings',
    label: 'SETTINGS',
    icon: <Cog6ToothIcon className="w-6 h-6" />,
  },
];

interface ProviderLayoutProps {
  children: React.ReactNode;
  /** Show a back arrow that navigates to this path (e.g. '/provider/catalog') */
  backTo?: string;
  /** Badge content for nav items, keyed by path */
  badges?: Record<string, React.ReactNode>;
}

const ProviderLayout: React.FC<ProviderLayoutProps> = ({ children, backTo, badges }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (item: NavItem) => {
    if (item.matchPrefixes) {
      return item.matchPrefixes.some((prefix) => location.pathname.startsWith(prefix));
    }
    return location.pathname === item.path;
  };

  return (
    <div className="min-h-screen">
      {/* Mobile hamburger menu toggle */}
      <div className="lg:hidden fixed top-20 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 bg-white/[0.06] border border-white/15 rounded-lg shadow-md hover:bg-white/[0.1] transition-colors"
        >
          {mobileMenuOpen ? (
            <XMarkIcon className="w-6 h-6 text-white/70" />
          ) : (
            <Bars3Icon className="w-6 h-6 text-white/70" />
          )}
        </button>
      </div>

      <div className="flex min-h-screen">
        {/* Sidebar Navigation */}
        <aside
          className={`
            fixed lg:sticky top-16 left-0 h-[calc(100vh-4rem)] border-r border-white/[0.08] transition-transform duration-300 z-40
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            lg:translate-x-0 w-64
          `}
        >
          <nav className="p-4">
            {/* Back arrow */}
            {backTo && (
              <button
                onClick={() => navigate(backTo)}
                className="w-full flex items-center gap-2 px-4 py-2 mb-3 text-white/50 hover:text-white text-sm rounded-lg hover:bg-white/[0.06] transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back</span>
              </button>
            )}

            <ul className="space-y-2">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item);
                const badge = badges?.[item.path];

                if (item.disabled) {
                  return (
                    <li key={item.label}>
                      <button className="w-full flex items-center gap-3 px-4 py-3 text-white/35 bg-transparent rounded cursor-not-allowed">
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                        <span className="ml-auto text-xs bg-white/10 text-white/30 px-2 py-0.5 rounded">Soon</span>
                      </button>
                    </li>
                  );
                }

                return (
                  <li key={item.path}>
                    <button
                      onClick={() => {
                        navigate(item.path);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded transition-all duration-300 ${
                        active
                          ? 'text-white bg-black hover:bg-gray-800'
                          : 'text-white/70 bg-transparent hover:bg-white/[0.06]'
                      }`}
                    >
                      {item.icon}
                      <span className="font-medium">{item.label}</span>
                      {badge && <span className="ml-auto">{badge}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default ProviderLayout;
